import { useEffect, useState } from 'react';
import {
  isCommandingSeat,
  INCIDENT_FUNCTIONAL_ROLES,
  type AgencyView,
  type IncidentCommand,
  type IncidentCommandParticipant,
  type IncidentCommandTransfer,
} from '@tsr/shared-types';
import {
  fetchAgencies,
  type AssumeCommandInput,
  type InviteToUnifiedCommandInput,
  type TransferCommandInput,
} from '../api';
import {
  INCIDENT_COMMAND_HEADING,
  UNIFIED_COMMAND_HEADING,
  LEAD_ORGANIZATION_LABEL,
  INCIDENT_COMMANDER_LABEL,
  ASSUME_COMMAND_LABEL,
  INVITE_TO_UNIFIED_COMMAND_LABEL,
  UNIFIED_COMMAND_INVITE_LABEL,
  TRANSFER_OF_COMMAND_LABEL,
  LEAD_OPS_SECTION_CHIEF_LABEL,
  OPERATIONS_BRANCH_LABEL,
  HANDOVER_BRIEFING_LABEL,
  SIGNED_AGREEMENT_LABEL,
  COMMAND_TRANSFER_WAYFINDING,
  operationsBranchLabel,
} from '../incidentVocabulary';

/**
 * W3 OWNERSHIP SPINE (#489) + UNIFIED COMMAND / TRANSFER (#492) — the SidePanel INCIDENT
 * COMMAND surface.
 *
 *   • IncidentCommandBlock — the READ. SINGLE command (N=1) reads identically to the spine:
 *     "Incident Command — <Lead Organization> · <Officer>". UNIFIED command (N≥2, §4.7) reads
 *     "Unified Command" with the full roster of Lead Organizations — the ONE lead flagged
 *     "Lead — Operations Section Chief", each co-lead with its Operations branch (Suppression /
 *     Community Protection) + officer + the signed agreement. A pending §4.8 hand-over shows as
 *     a "Transfer of Command" banner. ONE shared command, not N owners (§2.10).
 *   • AssumeCommandForm / InviteToUnifiedCommandForm / TransferOfCommandForm — the CAPTURE forms.
 *
 * Ontario IMS doctrine (docs/ontario-ims-doctrine-reference.md §1.2 / §2.9–§2.13; per-surface
 * §4.1 = UI strict verbatim). The commanding-seat gate (isCommandingSeat) filters every picker
 * to jurisdiction leads and excludes a coordinating EOC/PEOC/CAF (§4.2). The forbidden drift
 * terms are pinned absent by the vocabulary lock test.
 */

const TIER_LABEL: Record<string, string> = {
  municipal: 'Municipal',
  provincial: 'Provincial',
  first_nation: 'First Nation',
  federal: 'Federal',
};
function tierLabel(kind: string): string {
  return TIER_LABEL[kind] ?? kind.replace(/_/g, ' ');
}

/** The officer contact line — name · tel: · mailto: (scoped two-party disclosure). */
function OfficerContact({
  participant,
  testidPrefix,
}: {
  participant: IncidentCommandParticipant;
  testidPrefix: string;
}): JSX.Element | null {
  if (!participant.officer_name) return null;
  const tel = participant.officer_phone
    ? `tel:${participant.officer_phone.replace(/[^\d+]/g, '')}`
    : null;
  return (
    <p className="text-[11px] text-slate-400 mt-0.5">
      {/* #481 CLEANUP — the "who to reach" seat is the INCIDENT COMMANDER (App A #37), never a
          coordinating "duty officer" (§2.2/§2.9). Names the command officer's doctrine role. */}
      <span
        data-testid={`${testidPrefix}-officer-role`}
        className="mr-1 text-[9px] uppercase tracking-wide text-slate-500"
      >
        {INCIDENT_COMMANDER_LABEL}
      </span>
      <span data-testid={`${testidPrefix}-officer`}>{participant.officer_name}</span>
      {tel && (
        <>
          {' · '}
          <a
            data-testid={`${testidPrefix}-officer-tel`}
            href={tel}
            className="text-emerald-300 hover:underline"
            title={`Call ${participant.officer_name} at ${participant.officer_phone}`}
          >
            📞 {participant.officer_phone}
          </a>
        </>
      )}
      {participant.officer_email && (
        <>
          {' · '}
          <a
            data-testid={`${testidPrefix}-officer-mail`}
            href={`mailto:${participant.officer_email}`}
            className="text-emerald-300 hover:underline"
            title={`Email ${participant.officer_name}`}
          >
            ✉ email
          </a>
        </>
      )}
    </p>
  );
}

/** One roster row — a participating Lead Organization. The lead is flagged with the single
 *  "Lead — Operations Section Chief" chip (unified only); co-leads show their Operations branch. */
function ParticipantRow({
  participant,
  unified,
}: {
  participant: IncidentCommandParticipant;
  unified: boolean;
}): JSX.Element {
  const isLead = participant.is_lead;
  // The lead row keeps the spine's testids so single command (N=1) reads identically.
  const prefix = isLead ? 'command' : 'command-colead';
  const branch = operationsBranchLabel(participant.functional_role);
  return (
    <div
      className={unified ? 'mt-1.5 border-l-2 border-slate-700/70 pl-2' : 'mt-0.5'}
      data-testid={isLead ? 'command-lead-row' : 'command-colead-row'}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span
          data-testid={isLead ? 'command-lead-org' : 'command-colead-org'}
          className="text-xs font-medium text-slate-100"
        >
          {participant.agency_name}
        </span>
        <span
          data-testid={isLead ? 'command-tier-pill' : 'command-colead-tier-pill'}
          className="rounded bg-slate-700/60 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-slate-300"
          title={`${LEAD_ORGANIZATION_LABEL} jurisdiction: ${tierLabel(participant.agency_kind)}`}
        >
          {tierLabel(participant.agency_kind)}
        </span>
        {unified && isLead && (
          <span
            data-testid="command-lead-chief-chip"
            className="rounded bg-sky-700/50 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-sky-200"
            title="The one lead of unified command (Ontario IMS §4.7)"
          >
            {LEAD_OPS_SECTION_CHIEF_LABEL}
          </span>
        )}
        {branch && (
          <span
            data-testid="command-branch-pill"
            className="rounded bg-slate-700/40 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-slate-300"
            title={`${OPERATIONS_BRANCH_LABEL}: ${branch}`}
          >
            {branch}
          </span>
        )}
      </div>
      <OfficerContact participant={participant} testidPrefix={prefix} />
    </div>
  );
}

/** The pending command-change banner — one command throughout; nothing changes until the
 *  counterparty accepts. Kind-aware (migration 0147): a §4.8 transfer shows the hand-over briefing;
 *  a §4.7 unified invite shows the signed agreement + optional Operations branch (honest-pending —
 *  NO co-lead yet). */
function PendingTransferBanner({
  transfer,
}: {
  transfer: IncidentCommandTransfer;
}): JSX.Element {
  const isInvite = transfer.kind === 'unified_invite';
  const heading = isInvite ? UNIFIED_COMMAND_INVITE_LABEL : TRANSFER_OF_COMMAND_LABEL;
  const bodyLabel = isInvite ? SIGNED_AGREEMENT_LABEL : HANDOVER_BRIEFING_LABEL;
  const bodyText = isInvite ? transfer.agreement_note : transfer.handover_briefing;
  const branch = isInvite ? operationsBranchLabel(transfer.functional_role) : null;
  return (
    <div
      data-testid="command-pending-transfer"
      data-kind={transfer.kind}
      className="mt-2 rounded border border-amber-500/40 bg-amber-950/30 p-1.5"
    >
      <p className="text-[10px] uppercase tracking-wider text-amber-300">
        {heading} · pending
      </p>
      <p className="text-[11px] text-slate-300 mt-0.5">
        <span className="text-slate-400">{transfer.from_agency_name}</span>
        {' → '}
        <span data-testid="command-transfer-to" className="font-medium text-slate-100">
          {transfer.to_agency_name}
        </span>
        {transfer.to_officer_name && (
          <span className="text-slate-400"> · {transfer.to_officer_name}</span>
        )}
        {branch && (
          <span
            data-testid="command-transfer-branch"
            className="ml-1 rounded bg-slate-700/40 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-slate-300"
          >
            {branch}
          </span>
        )}
      </p>
      <p className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{bodyLabel}</p>
      <p
        data-testid="command-transfer-briefing"
        className="text-[11px] text-slate-300 whitespace-pre-wrap"
      >
        {bodyText}
      </p>
    </div>
  );
}

/** The READ block — who holds Incident Command. NULL until the incident is owned. */
export function IncidentCommandBlock({
  command,
}: {
  command: IncidentCommand | null;
}): JSX.Element | null {
  const lead = command?.lead ?? null;
  if (!command || !lead) return null;
  const unified = command.unified;
  const heading = unified ? UNIFIED_COMMAND_HEADING : INCIDENT_COMMAND_HEADING;
  // The §4.7 signed agreement — carried on a co-lead's participation.
  const agreement = command.participants.find((p) => p.agreement_note)?.agreement_note ?? null;
  return (
    <div className="mt-2" data-testid="incident-command-block">
      <p
        data-testid="command-heading"
        className="text-[10px] uppercase tracking-wider text-slate-500"
      >
        {heading}
      </p>
      {command.participants.map((p) => (
        <ParticipantRow key={p.agency_id} participant={p} unified={unified} />
      ))}
      {unified && agreement && (
        <p className="mt-1.5 text-[10px] text-slate-500">
          <span className="uppercase tracking-wider">{SIGNED_AGREEMENT_LABEL}</span>{' '}
          <span data-testid="command-agreement" className="text-slate-400">
            {agreement}
          </span>
        </p>
      )}
      {command.pending_transfer && <PendingTransferBanner transfer={command.pending_transfer} />}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Capture forms
 * ------------------------------------------------------------------ */

const inputCls =
  'w-full rounded border border-slate-600 bg-slate-800 px-1.5 py-1 text-[11px] text-slate-100 placeholder:text-slate-500';

/** Fetch the commanding-jurisdiction picker options (§4.1/§4.2), optionally excluding an agency
 *  already in command (the current lead). */
function useCommandingAgencies(excludeAgencyIds: string[] = []): AgencyView[] {
  const [agencies, setAgencies] = useState<AgencyView[]>([]);
  const excludeKey = excludeAgencyIds.join(',');
  useEffect(() => {
    const ctl = new AbortController();
    const exclude = new Set(excludeKey ? excludeKey.split(',') : []);
    void fetchAgencies(ctl.signal)
      .then((all) =>
        setAgencies(all.filter((a) => isCommandingSeat(a.kind, a.name) && !exclude.has(a.id))),
      )
      .catch(() => setAgencies([]));
    return () => ctl.abort();
  }, [excludeKey]);
  return agencies;
}

/** The ASSUME form — pick a LEAD ORGANIZATION (a commanding jurisdiction) + name the
 *  Incident Commander, then establish command. */
export function AssumeCommandForm({
  busy,
  onAssume,
  onCancel,
}: {
  busy: boolean;
  onAssume: (input: AssumeCommandInput) => void;
  onCancel: () => void;
}): JSX.Element {
  const agencies = useCommandingAgencies();
  const [agencyId, setAgencyId] = useState('');
  const [officerName, setOfficerName] = useState('');
  const [officerPhone, setOfficerPhone] = useState('');
  const [officerEmail, setOfficerEmail] = useState('');

  const canSubmit = agencyId !== '' && officerName.trim() !== '' && !busy;

  return (
    <form
      data-testid="assume-command-form"
      className="mt-1 space-y-1.5 rounded border border-sky-500/40 bg-slate-900/50 p-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        onAssume({
          agency_id: agencyId,
          officer_name: officerName.trim(),
          officer_phone: officerPhone.trim() || null,
          officer_email: officerEmail.trim() || null,
        });
      }}
    >
      <label className="block text-[10px] uppercase tracking-wider text-slate-500">
        {LEAD_ORGANIZATION_LABEL}
      </label>
      <select
        data-testid="assume-lead-org"
        value={agencyId}
        onChange={(e) => setAgencyId(e.target.value)}
        className={inputCls}
      >
        <option value="">Select a lead organization…</option>
        {agencies.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name} · {tierLabel(a.kind)}
          </option>
        ))}
      </select>
      <input
        data-testid="assume-officer-name"
        value={officerName}
        onChange={(e) => setOfficerName(e.target.value)}
        placeholder="Incident Commander name"
        className={inputCls}
      />
      <input
        data-testid="assume-officer-phone"
        value={officerPhone}
        onChange={(e) => setOfficerPhone(e.target.value)}
        placeholder="Contact phone (optional)"
        className={inputCls}
      />
      <input
        data-testid="assume-officer-email"
        value={officerEmail}
        onChange={(e) => setOfficerEmail(e.target.value)}
        placeholder="Contact email (optional)"
        className={inputCls}
      />
      <div className="flex items-center gap-2 pt-0.5">
        <button
          type="submit"
          data-testid="assume-command-submit"
          disabled={!canSubmit}
          className="text-[11px] px-2 py-1 rounded bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white font-medium"
        >
          {ASSUME_COMMAND_LABEL}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-[11px] px-2 py-1 rounded border border-slate-600 text-slate-300 hover:bg-slate-800"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

/** INVITE TO UNIFIED COMMAND (§4.7, #481 MUST-2) — the current lead INVITES a 2nd+ commanding
 *  Lead Organization to share command, with a functional Operations branch + the REQUIRED signed
 *  agreement. The co-lead establishes only when the invitee ACCEPTS (mutual §4.7 agreement — not a
 *  unilateral join). One shared command, not N owners. */
export function InviteToUnifiedCommandForm({
  busy,
  excludeAgencyIds,
  onInvite,
  onCancel,
}: {
  busy: boolean;
  excludeAgencyIds: string[];
  onInvite: (input: InviteToUnifiedCommandInput) => void;
  onCancel: () => void;
}): JSX.Element {
  const agencies = useCommandingAgencies(excludeAgencyIds);
  const [agencyId, setAgencyId] = useState('');
  const [role, setRole] = useState('');
  const [officerName, setOfficerName] = useState('');
  const [officerPhone, setOfficerPhone] = useState('');
  const [officerEmail, setOfficerEmail] = useState('');
  const [agreement, setAgreement] = useState('');

  const canSubmit = agencyId !== '' && officerName.trim() !== '' && agreement.trim() !== '' && !busy;

  return (
    <form
      data-testid="enter-unified-command-form"
      className="mt-1 space-y-1.5 rounded border border-sky-500/40 bg-slate-900/50 p-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        onInvite({
          agency_id: agencyId,
          agreement_note: agreement.trim(),
          functional_role: role || null,
          officer_name: officerName.trim(),
          officer_phone: officerPhone.trim() || null,
          officer_email: officerEmail.trim() || null,
        });
      }}
    >
      {/* #481 — issuer wayfinding: a command change targets another Lead Organization, not the PEOC. */}
      <p data-testid="unified-wayfinding" className="text-[10px] leading-snug text-slate-500">
        {COMMAND_TRANSFER_WAYFINDING}
      </p>
      <label className="block text-[10px] uppercase tracking-wider text-slate-500">
        {LEAD_ORGANIZATION_LABEL}
      </label>
      <select
        data-testid="unified-lead-org"
        value={agencyId}
        onChange={(e) => setAgencyId(e.target.value)}
        className={inputCls}
      >
        <option value="">Select a lead organization…</option>
        {agencies.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name} · {tierLabel(a.kind)}
          </option>
        ))}
      </select>
      <label className="block text-[10px] uppercase tracking-wider text-slate-500">
        {OPERATIONS_BRANCH_LABEL}
      </label>
      <select
        data-testid="unified-branch"
        value={role}
        onChange={(e) => setRole(e.target.value)}
        className={inputCls}
      >
        <option value="">Select an Operations branch (optional)…</option>
        {INCIDENT_FUNCTIONAL_ROLES.map((r) => (
          <option key={r} value={r}>
            {operationsBranchLabel(r)}
          </option>
        ))}
      </select>
      <input
        data-testid="unified-officer-name"
        value={officerName}
        onChange={(e) => setOfficerName(e.target.value)}
        placeholder="Incident Commander name"
        className={inputCls}
      />
      <input
        data-testid="unified-officer-phone"
        value={officerPhone}
        onChange={(e) => setOfficerPhone(e.target.value)}
        placeholder="Contact phone (optional)"
        className={inputCls}
      />
      <input
        data-testid="unified-officer-email"
        value={officerEmail}
        onChange={(e) => setOfficerEmail(e.target.value)}
        placeholder="Contact email (optional)"
        className={inputCls}
      />
      <label className="block text-[10px] uppercase tracking-wider text-slate-500">
        {SIGNED_AGREEMENT_LABEL} <span className="text-amber-400">(required)</span>
      </label>
      <textarea
        data-testid="unified-agreement"
        value={agreement}
        onChange={(e) => setAgreement(e.target.value)}
        placeholder="Agreement to unified command — verbally stated and signed in the written IAP (§4.7)"
        rows={2}
        className={inputCls}
      />
      <div className="flex items-center gap-2 pt-0.5">
        <button
          type="submit"
          data-testid="enter-unified-command-submit"
          disabled={!canSubmit}
          className="text-[11px] px-2 py-1 rounded bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white font-medium"
        >
          {INVITE_TO_UNIFIED_COMMAND_LABEL}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-[11px] px-2 py-1 rounded border border-slate-600 text-slate-300 hover:bg-slate-800"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

/** TRANSFER OF COMMAND (§4.8) — pick the receiving Lead Organization + name the receiving IC +
 *  write the REQUIRED detailed hand-over briefing. Command moves only on acknowledgement. */
export function TransferOfCommandForm({
  busy,
  excludeAgencyIds,
  onTransfer,
  onCancel,
}: {
  busy: boolean;
  excludeAgencyIds: string[];
  onTransfer: (input: TransferCommandInput) => void;
  onCancel: () => void;
}): JSX.Element {
  const agencies = useCommandingAgencies(excludeAgencyIds);
  const [agencyId, setAgencyId] = useState('');
  const [officerName, setOfficerName] = useState('');
  const [officerPhone, setOfficerPhone] = useState('');
  const [officerEmail, setOfficerEmail] = useState('');
  const [briefing, setBriefing] = useState('');

  const canSubmit = agencyId !== '' && officerName.trim() !== '' && briefing.trim() !== '' && !busy;

  return (
    <form
      data-testid="transfer-command-form"
      className="mt-1 space-y-1.5 rounded border border-amber-500/40 bg-slate-900/50 p-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        onTransfer({
          to_agency_id: agencyId,
          handover_briefing: briefing.trim(),
          to_officer_name: officerName.trim(),
          to_officer_phone: officerPhone.trim() || null,
          to_officer_email: officerEmail.trim() || null,
        });
      }}
    >
      {/* #481 — issuer wayfinding: Transfer of Command targets another Lead Organization, not the PEOC. */}
      <p data-testid="transfer-wayfinding" className="text-[10px] leading-snug text-slate-500">
        {COMMAND_TRANSFER_WAYFINDING}
      </p>
      <label className="block text-[10px] uppercase tracking-wider text-slate-500">
        {LEAD_ORGANIZATION_LABEL} <span className="text-slate-500">(receiving)</span>
      </label>
      <select
        data-testid="transfer-to-org"
        value={agencyId}
        onChange={(e) => setAgencyId(e.target.value)}
        className={inputCls}
      >
        <option value="">Select the receiving lead organization…</option>
        {agencies.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name} · {tierLabel(a.kind)}
          </option>
        ))}
      </select>
      <input
        data-testid="transfer-officer-name"
        value={officerName}
        onChange={(e) => setOfficerName(e.target.value)}
        placeholder="Receiving Incident Commander name"
        className={inputCls}
      />
      <input
        data-testid="transfer-officer-phone"
        value={officerPhone}
        onChange={(e) => setOfficerPhone(e.target.value)}
        placeholder="Contact phone (optional)"
        className={inputCls}
      />
      <input
        data-testid="transfer-officer-email"
        value={officerEmail}
        onChange={(e) => setOfficerEmail(e.target.value)}
        placeholder="Contact email (optional)"
        className={inputCls}
      />
      <label className="block text-[10px] uppercase tracking-wider text-slate-500">
        {HANDOVER_BRIEFING_LABEL} <span className="text-amber-400">(required)</span>
      </label>
      <textarea
        data-testid="transfer-briefing"
        value={briefing}
        onChange={(e) => setBriefing(e.target.value)}
        placeholder="Detailed hand-over briefing — objectives, resources, hazards, current status (§4.8)"
        rows={3}
        className={inputCls}
      />
      <div className="flex items-center gap-2 pt-0.5">
        <button
          type="submit"
          data-testid="transfer-command-submit"
          disabled={!canSubmit}
          className="text-[11px] px-2 py-1 rounded bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white font-medium"
        >
          {TRANSFER_OF_COMMAND_LABEL}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-[11px] px-2 py-1 rounded border border-slate-600 text-slate-300 hover:bg-slate-800"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
